import { ReactNode, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { api } from '../../services/api';
import Button from '../ui/Button';
import Modal from '../ui/Modal';
import Pill from '../ui/Pill';
import MoreMenu from '../ui/MoreMenu';
import { SkeletonRows, ErrorNotice, InfoNotice } from '../ui/States';
import { fmt } from '../../lib/format';
import { errorMessage, inputClass } from '../../lib/programs';
import { Audience, AudienceSourceKind, EventDay, SOURCE_LABEL, dayLabel, splitList } from '../../lib/events';
import Help from '../ui/Help';
import { th, td, headRow, num, em } from './logistics';

interface Group {
  id: string;
  name: string;
  memberCount: number;
}
interface BuildReport {
  name: string;
  candidates: number;
  alreadyListed: number;
  toAdd: number;
  toMove?: number;
  added?: number;
  moved?: number;
  restrictedSkipped?: number;
}

const EDITABLE: AudienceSourceKind[] = ['groups', 'filters', 'list', 'public'];

function Field({ title, children, wide = false }: { title: string; children: ReactNode; wide?: boolean }) {
  return (
    <div className={wide ? 'col-span-2 max-md:col-span-1' : ''}>
      <label className="block text-[15px] leading-5 font-semibold text-ink mb-[6px]">{title}</label>
      {children}
    </div>
  );
}

function whereFrom(a: Audience, groups: Group[]): string {
  const s = a.source;
  if (s.kind === 'groups') {
    const names = (s.groupIds ?? []).map((id) => groups.find((g) => g.id === id)?.name ?? 'a removed group');
    return [
      s.core ? 'Core members (PIs, Co-Is, staff, Fellows in their four years, committee members)' : null,
      ...names,
    ]
      .filter(Boolean)
      .join(' + ');
  }
  if (s.kind === 'filters') {
    const f = s.filters;
    return f
      ? [
          f.regions.length ? `region ${f.regions.join(' or ')}` : '',
          f.careerStages.length ? `career stage ${f.careerStages.join(' or ')}` : '',
          f.disciplines.length ? `discipline ${f.disciplines.join(' or ')}` : '',
        ]
          .filter(Boolean)
          .join('; ')
      : 'profile filters';
  }
  return SOURCE_LABEL[s.kind];
}

function AudienceForm({
  eventId,
  audience,
  days,
  groups,
  onClose,
  onSaved,
}: {
  eventId: string;
  audience: Audience | null;
  days: EventDay[];
  groups: Group[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const s = audience?.source;
  const [name, setName] = useState(audience?.name ?? '');
  const [kind, setKind] = useState<AudienceSourceKind>(s?.kind ?? 'list');
  const [core, setCore] = useState(s?.core ?? false);
  const [groupIds, setGroupIds] = useState<string[]>(s?.groupIds ?? []);
  const [regions, setRegions] = useState((s?.filters?.regions ?? []).join(', '));
  const [careerStages, setCareerStages] = useState((s?.filters?.careerStages ?? []).join(', '));
  const [disciplines, setDisciplines] = useState((s?.filters?.disciplines ?? []).join(', '));
  const [chosenDays, setChosenDays] = useState<string[]>(audience?.days ?? days.map((d) => d.date));
  const [funded, setFunded] = useState(audience?.funded ?? false);
  const [opensOn, setOpensOn] = useState(audience?.opensOn ?? '');
  const save = useMutation(
    async () => {
      const source =
        kind === 'groups'
          ? { kind, core, groupIds }
          : kind === 'filters'
            ? {
                kind,
                filters: {
                  regions: splitList(regions),
                  careerStages: splitList(careerStages),
                  disciplines: splitList(disciplines),
                },
              }
            : { kind };
      const body = {
        name,
        source,
        days: chosenDays.length === days.length ? null : chosenDays,
        funded,
        opensOn: opensOn || null,
      };
      return audience
        ? api.patch(`/events/${eventId}/audiences/${audience.key}`, body)
        : api.post(`/events/${eventId}/audiences`, body);
    },
    { onSuccess: onSaved },
  );
  const toggleDay = (date: string) =>
    setChosenDays((ds) => (ds.includes(date) ? ds.filter((d) => d !== date) : [...ds, date].sort()));
  const toggleGroup = (id: string) =>
    setGroupIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));
  return (
    <Modal
      title={audience ? `Edit “${audience.name}”` : 'New audience'}
      why="An audience gets its own invitation, attends its own days and has its travel covered or not."
      onClose={onClose}
      width={680}
      footer={
        <>
          <Button
            variant="primary"
            disabled={save.isLoading || !name.trim() || !chosenDays.length}
            onClick={() => save.mutate()}
          >
            {save.isLoading ? 'Saving…' : audience ? 'Save' : 'Add the audience'}
          </Button>
          <Button onClick={onClose}>Cancel</Button>
        </>
      }
    >
      <div className="grid grid-cols-2 gap-4 max-md:grid-cols-1">
        <Field title="Name the team will recognise" wide>
          <input
            className={inputClass}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Associate fellows, Partner organizations"
          />
        </Field>
        <Field title="Where the people come from" wide>
          <select className={inputClass} value={kind} onChange={(e) => setKind(e.target.value as AudienceSourceKind)}>
            {EDITABLE.map((k) => (
              <option key={k} value={k}>
                {SOURCE_LABEL[k]}
              </option>
            ))}
          </select>
        </Field>
        {kind === 'groups' ? (
          <div className="col-span-2 max-md:col-span-1 text-[15px]">
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={core} onChange={(e) => setCore(e.target.checked)} />
              Core members: PIs, Co-Is, staff, Fellows in their four years and this year&apos;s committee members (never
              anyone under 18)
            </label>
            <div className="text-ink-2 mt-3 mb-1">And these groups</div>
            <div className="grid grid-cols-2 max-md:grid-cols-1 gap-1 max-h-[180px] overflow-auto">
              {groups.map((g) => (
                <label key={g.id} className="flex items-center gap-2">
                  <input type="checkbox" checked={groupIds.includes(g.id)} onChange={() => toggleGroup(g.id)} />
                  {g.name} <span className="text-ink-2">({fmt(g.memberCount)})</span>
                </label>
              ))}
            </div>
          </div>
        ) : null}
        {kind === 'filters' ? (
          <>
            <Field title="Regions (any of)" wide>
              <input
                className={inputClass}
                value={regions}
                onChange={(e) => setRegions(e.target.value)}
                placeholder="Central, Atlantic"
              />
            </Field>
            <Field title="Career stages (any of)">
              <input
                className={inputClass}
                value={careerStages}
                onChange={(e) => setCareerStages(e.target.value)}
                placeholder="Doctoral, Postdoctoral"
              />
            </Field>
            <Field title="Disciplines (any of)">
              <input
                className={inputClass}
                value={disciplines}
                onChange={(e) => setDisciplines(e.target.value)}
                placeholder="Psychology"
              />
            </Field>
          </>
        ) : null}
        {kind === 'public' ? (
          <div className="col-span-2 max-md:col-span-1">
            <InfoNotice>
              Anyone who registers through the website form or Eventbrite, or walks in, and is not already on the list
              lands here. An event has exactly one such audience.
            </InfoNotice>
          </div>
        ) : null}
        <Field title="Days they attend" wide>
          <div className="flex flex-wrap gap-3 text-[15px]">
            {days.map((d) => (
              <label key={d.date} className="flex items-center gap-1">
                <input type="checkbox" checked={chosenDays.includes(d.date)} onChange={() => toggleDay(d.date)} />
                {dayLabel(d.date)}
                {d.label ? ` · ${d.label}` : ''}
              </label>
            ))}
          </div>
        </Field>
        <Field title="Travel">
          <label className="flex items-center gap-2 text-[15px] mt-1">
            <input type="checkbox" checked={funded} onChange={(e) => setFunded(e.target.checked)} />
            DIVERT covers their travel and hotel
          </label>
        </Field>
        <Field title="Invitation goes out on (for the timeline)">
          <input type="date" className={inputClass} value={opensOn} onChange={(e) => setOpensOn(e.target.value)} />
        </Field>
      </div>
      <p className="text-small text-ink-3 mt-3">
        {funded
          ? 'Their invitation says travel is covered and asks them to confirm through the form.'
          : 'Their invitation is a registration invitation; people who unsubscribed from bulk email are skipped.'}
      </p>
      {save.error ? (
        <div className="mt-3">
          <ErrorNotice>{errorMessage(save.error, 'Could not save the audience.')}</ErrorNotice>
        </div>
      ) : null}
    </Modal>
  );
}

function FillAudience({
  eventId,
  audience,
  onClose,
  onDone,
}: {
  eventId: string;
  audience: Audience;
  onClose: () => void;
  onDone: () => void;
}) {
  const run = useMutation(
    async (dryRun: boolean) =>
      (await api.post(`/events/${eventId}/audiences/${audience.key}/build`, { dryRun })).data.data as BuildReport,
    {
      onSuccess: (_d, dryRun) => {
        if (!dryRun) onDone();
      },
    },
  );
  return (
    <Modal
      title={`Fill “${audience.name}” from the platform`}
      why="Preview first: nothing is added until you confirm."
      onClose={onClose}
      footer={
        <>
          <Button
            variant={run.data ? 'secondary' : 'primary'}
            disabled={run.isLoading}
            onClick={() => run.mutate(true)}
          >
            Preview
          </Button>
          <Button
            variant={run.data ? 'primary' : 'secondary'}
            disabled={
              run.isLoading || !run.data || run.data.added !== undefined || !(run.data.toAdd || run.data.toMove)
            }
            onClick={() => run.mutate(false)}
          >
            {run.data?.toMove
              ? `Add ${run.data.toAdd}, move ${run.data.toMove}`
              : `Add ${run.data?.toAdd ?? ''} to the list`}
          </Button>
          <Button variant="quiet" onClick={onClose}>
            Close
          </Button>
        </>
      }
    >
      <p>
        Adds everyone the audience describes who is not on the list yet. People already on the list keep their audience,
        except those who registered publicly, who move into this one.
      </p>
      {run.data ? (
        <p className="text-table mt-3">
          {fmt(run.data.candidates)} found · {fmt(run.data.alreadyListed)} already on the list ·{' '}
          {run.data.added !== undefined ? `${fmt(run.data.added)} added` : `${fmt(run.data.toAdd)} to add`}
          {run.data.added !== undefined
            ? run.data.moved
              ? ` · ${fmt(run.data.moved)} moved from the public audience`
              : ''
            : run.data.toMove
              ? ` · ${fmt(run.data.toMove)} to move from the public audience`
              : ''}
          {run.data.restrictedSkipped ? ` · ${run.data.restrictedSkipped} under 18 skipped` : ''}
        </p>
      ) : null}
      {run.error ? (
        <div className="mt-3">
          <ErrorNotice>{errorMessage(run.error, 'Could not fill the audience.')}</ErrorNotice>
        </div>
      ) : null}
    </Modal>
  );
}

function RemoveAudience({
  eventId,
  audience,
  others,
  onClose,
  onDone,
}: {
  eventId: string;
  audience: Audience;
  others: Audience[];
  onClose: () => void;
  onDone: () => void;
}) {
  const [moveTo, setMoveTo] = useState(others[0]?.key ?? '');
  const people = audience.people ?? 0;
  const remove = useMutation(
    async () => api.delete(`/events/${eventId}/audiences/${audience.key}`, { params: people ? { moveTo } : undefined }),
    { onSuccess: onDone },
  );
  return (
    <Modal
      title={`Remove “${audience.name}”`}
      why="Nobody is removed from the list: their travel rules follow their new audience."
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Keep it</Button>
          <Button
            variant="destructive"
            className="ml-auto"
            disabled={remove.isLoading || (people > 0 && !moveTo)}
            onClick={() => remove.mutate()}
          >
            Remove the audience
          </Button>
        </>
      }
    >
      {people ? (
        <Field title={`Its ${fmt(people)} people move to`}>
          <select className={inputClass} value={moveTo} onChange={(e) => setMoveTo(e.target.value)}>
            {others.map((a) => (
              <option key={a.key} value={a.key}>
                {a.name}
              </option>
            ))}
          </select>
        </Field>
      ) : (
        <p>Nobody is in this audience.</p>
      )}
      {remove.error ? (
        <div className="mt-3">
          <ErrorNotice>{errorMessage(remove.error, 'Could not remove the audience.')}</ErrorNotice>
        </div>
      ) : null}
    </Modal>
  );
}

const WEEKDAY = (date: string) =>
  new Date(`${date}T12:00:00Z`).toLocaleDateString('en-CA', { timeZone: 'UTC', weekday: 'long' });

/** "every day", "Tuesday only", "Monday and Tuesday". */
function daysText(a: Audience, days: EventDay[]) {
  if (!a.days || a.days.length >= days.length) return 'every day';
  if (a.days.length === 1) return `${WEEKDAY(a.days[0])} only`;
  const names = a.days.map(WEEKDAY);
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

function summaryLine(a: Audience, days: EventDay[]) {
  return [
    `${fmt(a.people ?? 0)} invited`,
    `${fmt(a.coming ?? 0)} coming`,
    daysText(a, days),
    a.funded ? 'travel covered' : 'travel not covered',
  ].join(' · ');
}

/** Who the event invites, as audiences the Program Manager names: where people come from, days, funding, invitation date. */
export default function AudiencesPanel({
  eventId,
  canEdit,
  onChange,
}: {
  eventId: string;
  canEdit: boolean;
  onChange?: () => void;
}) {
  const queryClient = useQueryClient();
  const q = useQuery<{ audiences: Audience[]; days: EventDay[] }>(
    ['event-audiences', eventId],
    async () => (await api.get(`/events/${eventId}/audiences`)).data.data,
  );
  const groups = useQuery<Group[]>('groups', async () => (await api.get('/users/groups')).data.data, {
    enabled: canEdit,
  });
  const [managing, setManaging] = useState(false);
  const [editing, setEditing] = useState<Audience | 'new' | null>(null);
  const [filling, setFilling] = useState<Audience | null>(null);
  const [removing, setRemoving] = useState<Audience | null>(null);
  const changed = () => {
    queryClient.invalidateQueries(['event-audiences', eventId]);
    queryClient.invalidateQueries('event-invitees');
    onChange?.();
  };
  const reorder = useMutation(async (keys: string[]) => api.put(`/events/${eventId}/audiences/order`, { keys }), {
    onSuccess: changed,
  });
  if (q.isLoading) return <SkeletonRows rows={2} />;
  if (q.error || !q.data) return <ErrorNotice>{errorMessage(q.error, 'Could not load the audiences.')}</ErrorNotice>;
  const { audiences, days } = q.data;
  const move = (i: number, by: number) => {
    const keys = audiences.map((a) => a.key);
    const [k] = keys.splice(i, 1);
    keys.splice(i + by, 0, k);
    reorder.mutate(keys);
  };
  const allGroups = groups.data ?? [];
  const manageLabel = canEdit ? 'Manage audiences' : 'Audience details';
  const childOpen = Boolean(editing || filling || removing);
  return (
    <>
      <section
        aria-labelledby="audiences-head"
        className="mt-8 pt-4 border-t border-line-strong flex flex-wrap items-baseline gap-x-12 gap-y-3 max-md:hidden"
      >
        <h2 id="audiences-head" className="text-[15px] leading-[22px] font-semibold text-ink">
          Audiences
        </h2>
        <p className="flex-[1_1_520px] text-[15px] leading-[22px] text-ink-2">
          {audiences.map((a, i) => (
            <span key={a.key}>
              {i ? <br /> : null}
              <strong className="font-semibold text-ink">{a.name}</strong> {summaryLine(a, days)}
            </span>
          ))}
        </p>
        <Button onClick={() => setManaging(true)}>{manageLabel}</Button>
      </section>
      <details className="md:hidden mt-6 border-t border-line-strong border-b border-b-line">
        <summary className="cursor-pointer min-h-[44px] flex items-center text-[15px] leading-[22px] text-ink list-none [&::-webkit-details-marker]:hidden">
          <span className="font-semibold">Audiences</span>
          &nbsp;
          <span className="text-ink-3 truncate">
            · {audiences.map((a) => `${a.name} ${fmt(a.people ?? 0)}`).join(' · ')}
          </span>
        </summary>
        <p className="mb-3 text-[15px] leading-[22px] text-ink-2">
          {audiences.map((a) => (
            <span key={a.key}>
              <strong className="font-semibold text-ink">{a.name}</strong>: {summaryLine(a, days).replace(/ · /g, ', ')}
              .
              <br />
            </span>
          ))}
          <button
            type="button"
            className="text-accent underline underline-offset-[3px] hover:text-ink min-h-[44px]"
            onClick={() => setManaging(true)}
          >
            {manageLabel}
          </button>
        </p>
      </details>
      {managing && !childOpen ? (
        <Modal
          title="Audiences"
          why="Who this event invites. Each audience gets its own invitation, attends its own days and has its travel covered or not."
          onClose={() => setManaging(false)}
          width={980}
          footer={
            <>
              {canEdit ? (
                <Button variant="primary" onClick={() => setEditing('new')}>
                  Add an audience
                </Button>
              ) : null}
              <Button onClick={() => setManaging(false)}>Close</Button>
            </>
          }
        >
          <Help>
            Fill an audience from the platform to add everyone its groups or filters describe. Next year&apos;s event
            starts with the same audiences.
          </Help>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] tabular">
              <thead>
                <tr className={headRow}>
                  <th className={th}>Audience</th>
                  <th className={`${th} ${num}`}>People</th>
                  <th className={th}>Days</th>
                  <th className={th}>Travel</th>
                  <th className={th}>Invitation</th>
                  <th className={th}>
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {audiences.map((a, i) => (
                  <tr key={a.key} className="border-b border-line hover:bg-selected">
                    <td className={td}>
                      <span className="font-semibold">{a.name}</span>
                      <span className={em}>{whereFrom(a, allGroups)}</span>
                    </td>
                    <td className={`${td} ${num}`}>
                      {fmt(a.people ?? 0)}
                      <span className={em}>{fmt(a.coming ?? 0)} coming</span>
                    </td>
                    <td className={`${td} whitespace-nowrap`}>
                      {a.days ? a.days.map((d) => dayLabel(d).split(',')[0]).join(', ') : 'Every day'}
                    </td>
                    <td className={td}>
                      {a.funded ? <Pill tone="active">Covered</Pill> : <Pill tone="inactive">Not covered</Pill>}
                    </td>
                    <td className={td}>
                      {a.sentAt ? (
                        `Sent ${dayLabel(a.sentAt.slice(0, 10)).split(',')[0]} · ${fmt(a.sentCount)}`
                      ) : a.opensOn ? (
                        `Planned ${dayLabel(a.opensOn)}`
                      ) : (
                        <span className="text-ink-3">–</span>
                      )}
                    </td>
                    <td className={`${td} text-right whitespace-nowrap`}>
                      {canEdit ? (
                        <span className="inline-flex gap-2 items-center">
                          {a.source.kind === 'groups' || a.source.kind === 'filters' ? (
                            <Button size="sm" onClick={() => setFilling(a)}>
                              Fill from the platform
                            </Button>
                          ) : null}
                          <MoreMenu
                            label={`More for ${a.name}`}
                            items={[
                              { label: 'Edit', onClick: () => setEditing(a) },
                              {
                                label: 'Move up',
                                onClick: () => move(i, -1),
                                disabled: i === 0 || reorder.isLoading,
                              },
                              {
                                label: 'Move down',
                                onClick: () => move(i, 1),
                                disabled: i === audiences.length - 1 || reorder.isLoading,
                              },
                              ...(a.source.kind !== 'public' && a.key !== 'core'
                                ? [{ label: 'Remove', onClick: () => setRemoving(a), destructive: true }]
                                : []),
                            ]}
                          />
                        </span>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {reorder.error ? (
            <div className="mt-3">
              <ErrorNotice>{errorMessage(reorder.error, 'Could not reorder.')}</ErrorNotice>
            </div>
          ) : null}
        </Modal>
      ) : null}
      {editing ? (
        <AudienceForm
          eventId={eventId}
          audience={editing === 'new' ? null : editing}
          days={days}
          groups={allGroups}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            changed();
          }}
        />
      ) : null}
      {filling ? (
        <FillAudience eventId={eventId} audience={filling} onClose={() => setFilling(null)} onDone={changed} />
      ) : null}
      {removing ? (
        <RemoveAudience
          eventId={eventId}
          audience={removing}
          others={audiences.filter((a) => a.key !== removing.key)}
          onClose={() => setRemoving(null)}
          onDone={() => {
            setRemoving(null);
            changed();
          }}
        />
      ) : null}
    </>
  );
}
